import { useEffect, useState } from 'react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit } from 'lucide-react';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface UpdateMemberModalProps {
  name: string;
  currentRole: string;
  currentIsInfraAdmin?: boolean;
  showInfraAdmin?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  setIsUpdateMemberModalOpen: (isOpen: boolean) => void;
  handleSave: (chosenRole: string, isInfraAdmin: boolean) => void;
}

export const UpdateMemberModal = ({
  name,
  currentRole,
  currentIsInfraAdmin = false,
  showInfraAdmin = false,
  setIsUpdateMemberModalOpen,
  handleSave,
  isLoading = false,
  isOpen = false,
}: UpdateMemberModalProps) => {
  const [chosenRole, setChosenRole] = useState(currentRole);
  const [isInfraAdmin, setIsInfraAdmin] = useState(currentIsInfraAdmin);

  useEffect(() => {
    setChosenRole(currentRole);
    setIsInfraAdmin(currentIsInfraAdmin);
  }, [currentRole, currentIsInfraAdmin]);

  const Title = () => (
    <div className='flex items-center gap-2'>
      <Edit />
      <span id='org-members-update-title'>Update role for {name}</span>
    </div>
  );

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button id='org-members-update-cancel' variant='outline'>
          Cancel
        </Button>
      </DialogClose>
      <Button
        id='org-members-update-save'
        onClick={() => {
          handleSave(chosenRole, isInfraAdmin);
        }}
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Saving...
          </>
        ) : (
          `Save`
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(isOpen) => {
        setChosenRole(currentRole);
        setIsInfraAdmin(currentIsInfraAdmin);
        setIsUpdateMemberModalOpen(isOpen);
      }}
      title={<Title />}
      footer={<Footer />}
    >
      <RadioGroup
        value={chosenRole}
        className='mt-4'
        onValueChange={(value) => {
          setChosenRole(value);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='owner' id='org-members-update-owner' />
              <Label htmlFor='org-members-update-owner'>Owner</Label>
            </div>
          </TooltipTrigger>
          <TooltipContent side='left' className='max-w-[260px] text-wrap'>
            Owner permissions: add/remove members and change their roles across
            the org.
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='member' id='org-members-update-member' />
              <Label htmlFor='org-members-update-member'>Member</Label>
            </div>
          </TooltipTrigger>
          <TooltipContent side='left' className='max-w-[260px] text-wrap'>
            Collaborate on projects; can't invite/remove people or change roles.
          </TooltipContent>
        </Tooltip>
      </RadioGroup>
      {showInfraAdmin && (
        <div className='mt-4 flex items-center space-x-2 border-t pt-4'>
          <Checkbox
            id='org-members-update-infra-admin'
            checked={isInfraAdmin}
            onCheckedChange={(checked) => setIsInfraAdmin(checked === true)}
          />
          <Label htmlFor='org-members-update-infra-admin'>Infra Admin</Label>
        </div>
      )}
    </Modal>
  );
};
